import * as React from "react";
import { useNavigate } from "react-router-dom";
import { deleteUser, getUserById } from "../../api/users";
import { FaCoins, FaEye, FaPencilAlt, FaTrash } from "react-icons/fa";
import Modal from "../Submissions/components/Modal";
import Chip from "../../components/Chip";
import { MdEmojiEvents } from "react-icons/md";
import { IoNewspaperOutline } from "react-icons/io5";
import { TbBuildingFactory } from "react-icons/tb";
import { SlBadge } from "react-icons/sl";
import { TbUsersGroup } from "react-icons/tb";
import { IoLeafSharp } from "react-icons/io5";

function UserTable({ users, toast }) {

    const navigate = useNavigate();

    const [selectedRows, setSelectedRows] = React.useState(new Set());
    const [isModalOpen, setIsModalOpen] = React.useState(false);
    const [selectedUser, setSelectedUser] = React.useState(null);
    const [isUserLoading, setIsUserLoading] = React.useState(false);

    const getDisplayName = (user) => {
        if (!user) return '';
        const name = user.name;
        if (!name) return user.username || '';
        if (typeof name === 'string') return name;
        if (typeof name === 'object') {
            const parts = [];
            if (name.firstName) parts.push(name.firstName);
            if (name.middleName) parts.push(name.middleName);
            if (name.lastName) parts.push(name.lastName);
            return parts.join(' ') || user.username || '';
        }
        return String(name) || user.username || '';
    }

    const toggleSelectAll = () => {
        if (selectedRows.size === users.length) {
            setSelectedRows(new Set());
        } else {
            setSelectedRows(new Set(users.map(user => user._id)));
        }
    }; 

    const toggleSelectRow = (id) => {
        const newSelected = new Set(selectedRows);
        if (newSelected.has(id)) {
            newSelected.delete(id);
        } else {
            newSelected.add(id);
        }
        setSelectedRows(newSelected);
    };

    const handleEdit = (id, userType) => {
        // if (userType === 'user') navigate('/user/create/user', { state: { userId: id } });
        if (userType === 'user') navigate('/user/edit/user', { state: { userId: id } });
        else if (userType === 'ambassador') navigate('/user/create/ambassador', { state: { userId: id } });
        else if (userType === 'Company') navigate('/user/create/company', { state: { _id: id } });
        else if (userType === 'NGO') navigate('/user/create/ngo', { state: { _id: id } });
    }

    const handleDelete = (id) => {
        const con = window.confirm('Are you sure you want to delete this user?');
        con && deleteUser(id, toast);
    }

    const handleViewUser = async (user) => {
        // Fetch full user details (includes blogsSubmitted, proposedChallenges, badges etc.)
        setIsUserLoading(true);
        try {
            const fullUser = await getUserById(user._id);
            // getUserById returns the user object on success
            if (fullUser && fullUser._id) setSelectedUser(fullUser);
            else setSelectedUser(user);
        } catch (err) {
            // fallback to shallow user if API fails
            setSelectedUser(user);
        } finally {
            setIsUserLoading(false);
            setIsModalOpen(true);
        }
    }

    const closeModal = () => {
        setIsModalOpen(false);
        setSelectedUser(null);
    }

    if (users.length === 0) {
        return (
            <div className="w-full h-96 flex items-center justify-center">
                <h1 className="text-xl text-gray-400">No Users Found!</h1>
            </div>
        );
    }

    return (
        <div className="overflow-x-auto mt-6">
            <table className="w-full border-collapse text-base" role="table">
                <thead className="bg-navBtn">
                    <tr className="rounded-md">
                        <th className="p-3 text-left">
                            <input
                                type="checkbox"
                                className="h-4 w-4 rounded border-gray-300 accent-primary"
                                checked={selectedRows.size === users.length}
                                onChange={toggleSelectAll}
                                aria-label="Select all users"
                            />
                        </th>
                        <th className="p-3 text-left">Srl. No.</th>
                        <th className="p-3 text-left">Full Name</th>
                        <th className="p-3 text-left">User Name</th>
                        <th className="p-3 text-left">Email</th>
                        <th className="p-3 text-left">Own Referral Code</th>
                        <th className="p-3 text-left">Referred By</th>
                        <th className="p-3 text-left">Referred By Code</th>
                        <th className="p-3 text-left">City</th>
                        <th className="p-3 text-left">Age Group</th>
                        <th className="p-3 text-left">Join Date</th>
                        <th className="p-3 text-left">Status</th>
                        <th className="p-3 text-left">Actions</th>
                    </tr>
                </thead>
                <tbody>
                    {users.map((user, id) => (
                        <tr
                            key={user._id}
                            className="border-b border-navBtn hover:bg-gray-50"
                        >
                            <td className="p-3">
                                <input
                                    type="checkbox"
                                    className="h-4 w-4 rounded border-gray-300 accent-primary mt-1"
                                    checked={selectedRows.has(user._id)}
                                    onChange={() => toggleSelectRow(user._id)}
                                    aria-label={`Select ${user.name}`}
                                />
                            </td>
                            <td className="p-3">{id + 1}</td>
                            <td className="p-3">{getDisplayName(user) || 'N/A'}</td>
                            <td className="p-3">{user.username || 'N/A'}</td>
                            <td className="p-3">{user.email || 'N/A'}</td>
                            <td className="p-3">
                                {user.referralCode?.toUpperCase() || user.referral?.code?.toUpperCase() || 'N/A'}
                            </td>
                            <td className="p-3">{user.referredBy?.username || (user.referredBy?.referralCode ? 'deleted user' : 'N/A')}</td>
                            <td className="p-3">{user.referredBy?.referralCode?.toUpperCase() || 'N/A'}</td>
                            <td className="p-3">{user.location?.city || 'N/A'}</td>
                            <td className="p-3">{(user.ageGroup || '').charAt(0).toUpperCase() + (user.ageGroup || '').slice(1)}</td>
                            <td className="p-3">{new Date(user.createdAt).toLocaleDateString()}</td>
                            <td className="p-3">
                                <Chip value={user.active === 'true' || user.active === true ? 'Active' : 'Inactive'} color={user.active === 'true' || user.active === true ? 'green' : 'red'} />
                            </td>
                            <td className="p-3 flex flex-1">
                                <button className="rounded p-1.5 bg-[rgba(0,122,155,0.1)] hover:bg-[rgba(0,122,155,0.2)] min-h-7 mr-1"
                                    onClick={() => handleEdit(user._id, user.userType)}
                                >
                                    <FaPencilAlt className="fill-chipBlue" />
                                </button>
                                <button
                                    className="rounded p-1.5 bg-[rgba(52,199,89,0.1)] hover:bg-[rgba(52,199,89,0.2)] mx-1 min-h-7 min-w-7"
                                    title="View Request"
                                    onClick={() => handleViewUser(user)}
                                >
                                    <FaEye className="fill-primary" />
                                </button>
                                <button className="rounded p-1.5 bg-[rgba(250,22,22,0.1)] hover:bg-[rgba(250,22,22,0.2)] min-h-7 ml-1 px-2" onClick={() => handleDelete(user._id, user.userType)}>
                                    <FaTrash className="fill-red" />
                                </button>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>

            <Modal isOpen={isModalOpen} onClose={closeModal}>
                <div>
                    {isUserLoading ? (
                        <div className="w-full h-40 flex items-center justify-center">Loading...</div>
                    ) : (
                        selectedUser && (
                            <div>
                            <h2 className="text-2xl font-bold mb-6 text-primary">User Impact - {getDisplayName(selectedUser)}
                                <span className={`px-2 rounded-full text-xs ${selectedUser.active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                                    <Chip value={selectedUser.active ? 'Active' : 'Inactive'} color={selectedUser.active ? 'green' : 'red'} />
                                </span>
                            </h2> 

                            <div className='bg-gradient-to-r from-primary to-chipBlue shadow-md rounded-lg p-6 mb-6'>
                                <p className='text-white text-xl'>@{selectedUser.username}</p>
                                <span className='text-white text-base'>{selectedUser.email}</span>
                                <span className='ml-3 text-white text-base'>{selectedUser.mobile || 'Not provided'}</span>
                                <p className='text-white text-base'>Joined on: {selectedUser.createdAt?.toRegularDate ? selectedUser.createdAt.toRegularDate() : 'N/A'}</p>
                            </div>

                            <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                                <div className='bg-white border-l-4 border-primary shadow-sm rounded-lg p-4'>
                                    <div className='flex items-center justify-between'>
                                        <div>
                                            <p className='text-base font-medium text-secondary'>Total Earned Points</p>
                                            <p className='text-3xl font-bold text-primary'>{selectedUser.points || 0}</p>
                                        </div>
                                        <div className='bg-green-100 rounded-full p-3'>
                                            <FaCoins className="size-6 fill-primary" />
                                        </div>
                                    </div>
                                </div>

                                <div className='bg-white border-l-4 border-primary shadow-sm rounded-lg p-4'>
                                    <div className='flex items-center justify-between'>
                                        <div>
                                            <p className='text-base font-medium text-secondary'>Total Challenges Participated</p>
                                            <p className='text-3xl font-bold text-chipBlue'>{selectedUser.challengesParticipated?.length || 0}</p>
                                        </div>
                                        <div className='bg-blue-100 rounded-full p-3'>
                                            <MdEmojiEvents className="size-6 fill-chipBlue" />
                                        </div>
                                    </div>
                                </div>

                                <div className='bg-white border-l-4 border-primary shadow-sm rounded-lg p-4'>
                                    <div className='flex items-center justify-between'>
                                        <div>
                                            <p className='text-base font-medium text-secondary'>Total Challenges Proposed</p>
                                            <p className='text-3xl font-bold text-chipPurple'>{selectedUser.challengesCreated?.length || 0}</p>
                                        </div>
                                        <div className='bg-purple-100 rounded-full p-3'>
                                            <svg className='w-6 h-6 text-chipPurple' fill='currentColor' viewBox='0 0 20 20'>
                                                <path d='M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z'></path>
                                            </svg>
                                        </div>
                                    </div>
                                </div>

                                <div className='bg-white border-l-4 border-primary shadow-sm rounded-lg p-4'>
                                    <div className='flex items-center justify-between'>
                                        <div>
                                            <p className='text-base font-medium text-secondary'>Total Events Attended</p>
                                            <p className='text-3xl font-bold text-chipYellow'>{selectedUser.events?.length || 0}</p>
                                        </div>
                                        <div className='bg-orange-100 rounded-full p-3'>
                                            <svg className='w-6 h-6 text-chipYellow' fill='currentColor' viewBox='0 0 20 20'>
                                                <path d='M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zM4 7h12v9a1 1 0 01-1 1H5a1 1 0 01-1-1V7z'></path>
                                            </svg>
                                        </div>
                                    </div>
                                </div>

                                <div className='bg-white border-l-4 border-primary shadow-sm rounded-lg p-4'>
                                    <div className='flex items-center justify-between'>
                                        <div>
                                            <p className='text-base font-medium text-secondary'>Total Blogs Submitted</p>
                                            <p className='text-3xl font-bold text-chipMagenta'>{selectedUser.blogsSubmitted?.length || 0}</p>
                                        </div>
                                        <div className='bg-indigo-100 rounded-full p-3'>
                                            <IoNewspaperOutline className="size-6 fill-chipMagenta text-chipMagenta" />
                                        </div>
                                    </div>
                                </div>

                                <div className='bg-white border-l-4 border-primary shadow-sm rounded-lg p-4'>
                                    <div className='flex items-center justify-between'>
                                        <div>
                                            <p className='text-base font-medium text-secondary'>Total Carbon Savings</p>
                                            <p className='text-3xl font-bold text-chipRed'>{selectedUser.carbonKgSaved || 0} <span className='text-sm font-normal'>KG</span></p>
                                        </div>
                                        <div className='bg-green-100 rounded-full p-3'>
                                            <TbBuildingFactory className="size-6 fill-chipRed text-chipRed" />
                                        </div>
                                    </div>
                                </div>

                                {/* Total Trees Planted */}
                                <div className='bg-white border-l-4 border-primary shadow-sm rounded-lg p-4'>
                                    <div className='flex items-center justify-between'>
                                        <div>
                                            <p className='text-base font-medium text-secondary'>Total Trees Planted</p>
                                            <p className='text-3xl font-bold text-primary'>{selectedUser.treesPlanted || selectedUser.totalTreesPlanted || 0}</p>
                                        </div>
                                        <div className='bg-green-100 rounded-full p-3'>
                                            <IoLeafSharp className="size-6 fill-primary text-primary" />
                                        </div>
                                    </div>
                                </div>

                                <div className='bg-white border-l-4 border-primary shadow-sm rounded-lg p-4'>
                                    <div className='flex items-center justify-between'>
                                        <div>
                                            <p className='text-base font-medium text-secondary'>Total Badges Earned</p>
                                            <p className='text-2xl font-bold text-orange'>{selectedUser.badgesEarnedCount || 0} </p>
                                        </div>
                                        <div className='bg-green-100 rounded-full p-3'>
                                            <SlBadge className="size-6 fill-orange text-orange" />
                                        </div>
                                    </div>
                                </div>
                                <div className='bg-white border-l-4 border-primary shadow-sm rounded-lg p-4'>
                                    <div className='flex items-center justify-between'>
                                        <div>
                                            <p className='text-base font-medium text-secondary'>New user referrals</p>
                                            <p className='text-2xl font-bold text-chipBlue'>{selectedUser.referralsCount || 0} </p>
                                        </div>
                                        <div className='bg-green-100 rounded-full p-3'>
                                            <TbUsersGroup className="size-6 fill-chipBlue text-chipBlue" />
                                        </div>
                                    </div>
                                </div>
                                <div className='bg-white border-l-4 border-primary shadow-sm rounded-lg p-4'>
                                    <div className='flex items-center justify-between'>
                                        <div>
                                            <p className='text-base font-medium text-secondary'>Eco Activities Posted</p>
                                            <p className='text-2xl font-bold text-primary'>{selectedUser.ecoActivitiesCount || 0} </p>
                                        </div>
                                        <div className='bg-green-100 rounded-full p-3'>
                                            <IoLeafSharp className="size-6 fill-primary text-primary" />
                                        </div>
                                    </div>
                                </div>
                            </div>
                            </div>
                        )
                    )}
                </div>
            </Modal>
        </div>
    );
}

export default UserTable;